/**
 * Early, small bundle: GTM + Umbraco Engage after Cookiebot consent.
 * GTM loads as soon as consent is known and `view_page` has been pushed; Umbraco Engage, and GTM
 * as a fallback, keep the page load/delay schedule. Cookiebot itself loads early from Head.cshtml.
 */
import { scheduleAfterLoadOrDelay } from './modules/third-party-defer';
import { prepareCookiebotListeners } from './modules/cookie-bot';
import { prepareRakutenConsentRecapture } from './modules/rakuten-loyalty';

declare global {
  interface Window {
    __deferredThirdPartyInit?: boolean;
    __tsPageViewPushed?: boolean;
    Cookiebot?: {
      consent?: {
        preferences: boolean;
        statistics: boolean;
        marketing: boolean;
      };
      hasResponse?: boolean;
      renew?: () => void;
    };
  }
}

const CONFIG_ID = 'deferred-third-party-config';
const UMBRACO_ENGAGE_SCRIPTS_JSON_ID = 'umbraco-engage-scripts-json';

export type DeferredThirdPartyConfig = {
  gtmId?: string | null;
  cookiebotCbid?: string | null;
  umbracoEngageScripts?: string[];
};

function readConfig(): DeferredThirdPartyConfig {
  let base: DeferredThirdPartyConfig = {};
  const el = document.getElementById(CONFIG_ID);
  if (el?.textContent?.trim()) {
    try {
      base = JSON.parse(el.textContent) as DeferredThirdPartyConfig;
    } catch {
      base = {};
    }
  }
  const engageEl = document.getElementById(UMBRACO_ENGAGE_SCRIPTS_JSON_ID);
  if (engageEl?.textContent?.trim()) {
    try {
      base.umbracoEngageScripts = JSON.parse(engageEl.textContent) as string[];
    } catch {
      /* ignore */
    }
  }
  return base;
}

let gtmInjected = false;

function injectGtm(gtmId: string): void {
  // Both the early path and the load/delay fallback call this; the container must load once.
  if (gtmInjected) {
    return;
  }
  gtmInjected = true;
  const w = window as Window & { dataLayer?: unknown[] };
  const d = document;
  const l = 'dataLayer';
  w[l] = w[l] || [];
  (w[l] as unknown[]).push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  const f = d.getElementsByTagName('script')[0]!;
  const j = d.createElement('script');
  const dl = l !== 'dataLayer' ? '&l=' + l : '';
  j.async = true;
  j.src = 'https://www.googletagmanager.com/gtm.js?id=' + encodeURIComponent(gtmId) + dl;
  // Auto-blocking must not hold back the container; tags still respect Consent Mode / Cookiebot events.
  j.setAttribute('data-cookieconsent', 'ignore');
  f.parentNode!.insertBefore(j, f);
}

function injectUmbracoEngage(urls: string[]): void {
  urls.forEach((src) => {
    const s = document.createElement('script');
    s.src = src;
    s.defer = true;
    document.head.appendChild(s);
  });
}

function injectGtmAndUmbraco(config: DeferredThirdPartyConfig): void {
  const gtmId = config.gtmId?.trim();
  if (gtmId) {
    injectGtm(gtmId);
  }
  const urls = config.umbracoEngageScripts;
  if (urls?.length) {
    injectUmbracoEngage(urls);
  }
}

/**
 * Resolves once Cookiebot has a consent response (prior cookie or banner choice).
 * Falls back if Cookiebot never becomes available (script failure), unless `stallFallback` is false.
 */
function whenCookiebotConsentResolved(onReady: () => void, stallFallback = true): void {
  let ran = false;
  const run = (): void => {
    if (ran) {
      return;
    }
    ran = true;
    onReady();
  };

  const tryHasResponse = (): boolean => {
    if (window.Cookiebot?.hasResponse) {
      run();
      return true;
    }
    return false;
  };

  if (tryHasResponse()) {
    return;
  }

  const onEvent = (): void => {
    run();
  };

  window.addEventListener('CookiebotOnAccept', onEvent, { once: true });
  window.addEventListener('CookiebotOnDecline', onEvent, { once: true });
  window.addEventListener('CookiebotOnConsentReady', onEvent, { once: true });

  if (!stallFallback) {
    return;
  }

  let attempts = 0;
  const pollId = window.setInterval(() => {
    attempts++;
    if (tryHasResponse() || attempts >= 200) {
      window.clearInterval(pollId);
      if (attempts >= 200 && !ran) {
        // Cookiebot failed or stalled — still load GTM; Consent Mode defaults stay denied.
        run();
      }
    }
  }, 50);
}

/** Runs once `view_page` is in the dataLayer (signalled by `pageViewEvent` in scripts.js). */
function whenPageViewPushed(onReady: () => void): void {
  if (window.__tsPageViewPushed) {
    onReady();
    return;
  }
  window.addEventListener('ts:page-view-pushed', () => onReady(), { once: true });
}

function ensureDataLayer(): void {
  const w = window as Window & { dataLayer?: unknown[] };
  if (!Array.isArray(w.dataLayer)) {
    w.dataLayer = [];
  }
}

function bootstrap(): void {
  window.__deferredThirdPartyInit = true;
  ensureDataLayer();
  const config = readConfig();
  const cbid = config.cookiebotCbid?.trim();

  if (cbid) {
    prepareCookiebotListeners();
    // Registered before the deferral below: consent is often given well before the 5s GTM delay.
    prepareRakutenConsentRecapture();
  }

  // Early GTM: once consent is known and view_page is pushed, so early sessions and clicks are
  // tracked without Page View tags firing ahead of the page data. No stall fallback here: a
  // visitor who has not answered the banner keeps the load/delay timing below.
  const gtmId = config.gtmId?.trim();
  if (gtmId) {
    whenPageViewPushed(() => {
      if (cbid) {
        whenCookiebotConsentResolved(() => injectGtm(gtmId), false);
      } else {
        injectGtm(gtmId);
      }
    });
  }

  // Load/5s deferral: Umbraco Engage, and GTM if the early path has not run.
  scheduleAfterLoadOrDelay(() => {
    if (cbid) {
      whenCookiebotConsentResolved(() => injectGtmAndUmbraco(config));
    } else {
      injectGtmAndUmbraco(config);
    }
  });
}

bootstrap();
